import PropTypes from 'prop-types';
import React, { useContext, useMemo } from 'react';
import _ from 'underscore';
import hoistNonReactStatics from 'hoist-non-react-statics';
import { getDisplayName } from '@bingads-webui-react/hoc-utils';

import { PermissionsContext } from '@bingads-webui-react/hoc-with-permissions';
import { getCustomizedToolsBar, smartImportBar } from '@bingads-webui-react/global-menu';
import { withFlatSmartToolsSiteMap } from '@bingads-webui-react/with-site-map';

export const withSmartConfig = (WrappedComponent) => {
  const HoC = (props) => {
    const permissions = useContext(PermissionsContext);

    const isSmartMode = props.isAccountSummary ? permissions.ManagesOnlySmartAccounts :
      permissions.IsSmartMode;

    // Not showing customized import menu in customer level
    const isSmartCampaignsImportEnabled = !props.isAccountSummary && (_.result(permissions.dynamic, 'SmartCampaignsImport', false) || permissions.IsSmartCampaignsImportEnabled);

    // If not smart mode, show original import menu.
    // If is smart mode, check the value of isSmartCampaignsImportEnabled
    const hideImport = isSmartMode ? !isSmartCampaignsImportEnabled : false;

    const hide = {
      import: hideImport,
      reports: isSmartMode,
      searchBar: isSmartMode,
      myWebsite: true,
      ...props.hide,
      assetlibrary: true,
    };

    const SmartModeAccountToolsBar = useMemo(
      () => (isSmartMode ?
        getCustomizedToolsBar({
          TopMenuComponent: props.TopMenuComponent,
          siteMapHoc: withFlatSmartToolsSiteMap,
          siteMapRootId: ['app', 'setup'],
        }) :
        props.CustomizedToolsBar),
      [isSmartMode, props.CustomizedToolsBar, props.TopMenuComponent]
    );

    return (<WrappedComponent
      {...props}
      CustomizedToolsBar={SmartModeAccountToolsBar}
      CustomizedImportBar={isSmartMode && isSmartCampaignsImportEnabled ?
        smartImportBar : props.CustomizedImportBar}
      hide={hide}
      isSearchBarAsDropdownEnabled={!isSmartMode && permissions.dynamic.SearchBarAsDropdown}
    />);
  };

  HoC.displayName = `withSmartConfig(${getDisplayName(WrappedComponent)})`;
  HoC.propTypes = {
    serverContext: PropTypes.shape({}).isRequired,
    account: PropTypes.shape({
      Id: PropTypes.number.isRequired,
      Name: PropTypes.string.isRequired,
      AccountNumber: PropTypes.string.isRequired,
      ParentCustomerId: PropTypes.number.isRequired,
      AccountMode: PropTypes.number,
    }).isRequired,
    CustomizedToolsBar: PropTypes.elementType,
    CustomizedImportBar: PropTypes.elementType,
    TopMenuComponent: PropTypes.elementType,
    hide: PropTypes.shape({
      accountBreadcrumb: PropTypes.bool,
      searchBar: PropTypes.bool,
      customerSelector: PropTypes.bool,
    }),
    isAccountSummary: PropTypes.bool,
    isSearchBarAsDropdownEnabled: PropTypes.bool,
  };

  HoC.defaultProps = {
    CustomizedToolsBar: null,
    CustomizedImportBar: null,
    TopMenuComponent: undefined,
    hide: {},
    isAccountSummary: false,
    isSearchBarAsDropdownEnabled: false,
  };

  return hoistNonReactStatics(HoC, WrappedComponent);
};
